import { useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import UIDateInput from 'UIComponents/dates/UIDateInput';
// @ts-expect-error untyped module

import EnrollmentSchedule from 'automation-ui-enrollment/records/EnrollmentSchedule';
import { ONCE, DAILY, WEEKLY, MONTHLY, YEARLY, CadenceTranslationKeys } from 'automation-ui-enrollment/constants/scheduledEnrollment/ScheduleCadence';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { useHasScope } from 'automation-ui-core/hooks/authHooks';
import { SCHEDULED_ENROLLMENT_ACCESS } from 'automation-ui-enrollment/constants/Scopes';
import { DayOfWeekTranslationKeys, DayOfWeekValues } from 'automation-ui-enrollment/constants/scheduledEnrollment/DayOfWeekValues';
import I18n from 'I18n';
import DayOfMonthSelect from 'automation-ui-enrollment/components/scheduledEnrollment/DayOfMonthSelect';
import TimeDeltaSelect from 'automation-ui-enrollment/components/scheduledEnrollment/TimeDeltaSelect';
import MaybeDataTokenInput from 'automation-ui-formula-editor/data-passing-panel/Inputs/maybeDataTokenInput/MaybeDataTokenInput';
import { ENROLLED_OBJECT } from 'automation-ui-formula-editor/data-sources/constants/dataSourcesConstants';
import RecurringDateInput from 'automation-ui-enrollment/components/scheduledEnrollment/RecurringDateInput';
import TimeOfDaySelect from 'automation-ui-enrollment/components/scheduledEnrollment/TimeOfDaySelect';
import PortalIdParser from 'PortalIdParser';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EnrollmentScheduleSelect = ({
  schedule,
  onChange,
  readOnly: isScheduleReadOnly,
  controlUse = 'default',
  showTimeZone = true,
  isDataset = false
}) => {
  const hasScheduledTriggersScope = useHasScope(SCHEDULED_ENROLLMENT_ACCESS);
  const {
    openUpgradeModal
  } = useUpgradeModal('workflows-trigger-scheduled');
  const isLocked = useCallback(cadenceToCheck => !hasScheduledTriggersScope && [DAILY, WEEKLY, MONTHLY].includes(cadenceToCheck), [hasScheduledTriggersScope]);
  const handleScheduleWindowChange = useCallback(({
    target: {
      value
    }
  }) => {
    onChange(syntheticEvent(schedule.setScheduleWindow(value)));
  }, [onChange, schedule]);
  const cadence = schedule.getCadence();
  const handleCadenceChange = useCallback(({
    target: {
      value
    }
  }) => {
    if (isLocked(value)) {
      openUpgradeModal();
    } else {
      onChange(syntheticEvent(schedule.setCadence(value).setScheduleWindow(value === ONCE ? 'SPECIFIC_TIME' : EnrollmentSchedule.getDefaultScheduleWindowForPortal(PortalIdParser.get()))));
    }
  }, [onChange, schedule, isLocked, openUpgradeModal]);
  const handleDayOfWeekChange = useCallback(({
    target: {
      value: values
    }
  }) => {
    /* expected type of `value` is an array so renamed to values */
    onChange(syntheticEvent(schedule.setDayOfWeekValues(values)));
  }, [onChange, schedule]);
  const handleDateChange = useCallback(({
    target: {
      value
    }
  }) => {
    if (value) {
      onChange(syntheticEvent(schedule.setSimpleDate(value)));
    } else {
      onChange(syntheticEvent(schedule.setCadence(ONCE)));
    }
  }, [onChange, schedule]);
  const handleRecurringDateChange = useCallback(({
    target: {
      value
    }
  }) => {
    if (value) {
      onChange(syntheticEvent(schedule.setMonthValues([value.month]).setDayOfMonthValues([value.dayOfMonth])));
    } else {
      onChange(syntheticEvent(schedule.setCadence(YEARLY)));
    }
  }, [onChange, schedule]);
  const handleClearDateProperty = useCallback(() => {
    onChange(syntheticEvent(schedule.setCadence(cadence)));
  }, [onChange, schedule, cadence]);
  const handleDatePropertyChange = useCallback(({
    dataProperty
  }) => {
    onChange(syntheticEvent(schedule.setDateProperty(dataProperty.name)));
  }, [onChange, schedule]);
  const handleTimeDeltaChange = useCallback(timeDelta => {
    onChange(syntheticEvent(schedule.setTimeDelta(timeDelta)));
  }, [onChange, schedule]);
  const handleTimeChange = useCallback(({
    target: {
      value
    }
  }) => {
    onChange(syntheticEvent(schedule.setTimeFromMinutes(Number(value))));
  }, [onChange, schedule]);

  // hack to avoid embedded the data panel in the create page
  const isDataPanelAvailable = controlUse === 'default';
  const cadenceOptions = [ONCE, YEARLY, MONTHLY, WEEKLY, DAILY].map(value => ({
    text: I18n.text(CadenceTranslationKeys[value].titleKey),
    value,
    icon: isLocked(value) ? 'locked' : undefined
  }));
  const readOnly = isScheduleReadOnly || isLocked(cadence);

  // UIFormControl requires that its `children` prop type not be null
  let DateInput = /*#__PURE__*/_jsx("span", {});
  if (cadence === ONCE && !schedule.dateProperty) {
    DateInput = /*#__PURE__*/_jsx(UIDateInput, {
      use: controlUse,
      "data-test-id": "select-schedule-date",
      onChange: handleDateChange,
      readOnly: readOnly,
      value: schedule.getSimpleDate()
    });
  } else if (cadence === YEARLY && !schedule.dateProperty) {
    DateInput = /*#__PURE__*/_jsx(RecurringDateInput, {
      use: controlUse,
      readOnly: readOnly,
      value: {
        month: schedule.month.values[0],
        dayOfMonth: schedule.dayOfMonth.values[0]
      },
      onChange: handleRecurringDateChange
    });
  }
  const datePropertyToken = schedule.dateProperty && `{{ ${ENROLLED_OBJECT}.${schedule.dateProperty.propertyName} }}`;
  const selectButtonUse = controlUse === 'on-dark' ? 'form-on-dark' : 'form';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UIFormControl, {
      required: true,
      label: isDataset ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.scheduleInput.scheduleDataset"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.scheduleInput.schedule"
      }),
      children: /*#__PURE__*/_jsx(UISelect, {
        "data-test-id": "select-schedule-cadence",
        buttonUse: selectButtonUse,
        className: "display-inline-block",
        value: cadence,
        onChange: handleCadenceChange,
        readOnly: isScheduleReadOnly,
        options: cadenceOptions
      })
    }), cadence === WEEKLY && /*#__PURE__*/_jsx(UIFormControl, {
      required: true,
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.scheduleInput.dayOfWeek.title"
      }),
      children: /*#__PURE__*/_jsx(UISelect, {
        "data-test-id": "select-schedule-day-of-week",
        buttonUse: selectButtonUse,
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.scheduleInput.dayOfWeek.placeholder"
        }),
        className: "display-inline-block",
        defaultValue: "1",
        multi: true,
        anchorType: "button",
        value: [...schedule.dayOfWeek.values].sort(),
        onChange: handleDayOfWeekChange,
        readOnly: readOnly,
        options: Object.values(DayOfWeekValues).map(value => {
          return {
            text: I18n.text(DayOfWeekTranslationKeys[value]),
            value
          };
        })
      })
    }), cadence === MONTHLY && /*#__PURE__*/_jsx(UIFormControl, {
      required: true,
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.scheduleInput.dayOfMonth.title"
      }),
      children: /*#__PURE__*/_jsx(DayOfMonthSelect, {
        selectProps: {
          readOnly,
          buttonUse: selectButtonUse,
          anchorType: 'button'
        },
        onChange: onChange,
        schedule: schedule,
        readOnly: readOnly
      })
    }), (cadence === ONCE || cadence === YEARLY) && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UIFormControl, {
        required: true,
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.scheduleInput.date"
        }),
        children: isDataPanelAvailable ? /*#__PURE__*/_jsx(MaybeDataTokenInput, {
          dataSourceAllowList: [ENROLLED_OBJECT],
          heightOffsetAdjustment: 140,
          hideNestedDataSources: true,
          maybeDataToken: datePropertyToken !== null && datePropertyToken !== void 0 ? datePropertyToken : undefined,
          onClearInput: handleClearDateProperty,
          onDataPropertySelected: handleDatePropertyChange,
          propertyTypeAllowList: ['date', 'datetime'],
          readOnly: readOnly,
          requiredPermissionLevel: "read",
          StaticInput: DateInput
        }) : DateInput
      }), schedule.dateProperty && /*#__PURE__*/_jsx(TimeDeltaSelect, {
        timeDelta: schedule.dateProperty.delta,
        onChange: handleTimeDeltaChange,
        readOnly: readOnly
      })]
    }), /*#__PURE__*/_jsx(TimeOfDaySelect, {
      controlUse: controlUse,
      readOnly: readOnly,
      schedule: schedule,
      onTimeChange: handleTimeChange,
      onScheduleWindowChange: handleScheduleWindowChange,
      showTimeZone: showTimeZone
    })]
  });
};
export default EnrollmentScheduleSelect;